import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem('authToken')) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const fundingListsAPI = {
  // Get all lists (personal + farm), grouped
  async getLists() {
    const response = await api.get('/funding-lists/');
    return response.data;
  },

  // Create a new list
  async createList(name, description = '', farmId = null) {
    const response = await api.post('/funding-lists/', { name, description, farm: farmId });
    return response.data;
  },

  // Delete a list
  async deleteList(listId) {
    const response = await api.delete(`/funding-lists/${listId}/`);
    return response.data;
  },

  // Save to default Saved list
  async saveOpportunity(fundingId, notes = '') {
    const response = await api.post(`/funding-schemes/${fundingId}/save_opportunity/`, { notes });
    return response.data;
  },

  // Remove from default Saved list
  async unsaveOpportunity(fundingId) {
    const response = await api.delete(`/funding-schemes/${fundingId}/unsave_opportunity/`);
    return response.data;
  },

  // Add to a specific list
  async addToList(fundingId, listId, notes = '') {
    const response = await api.post(`/funding-schemes/${fundingId}/add-to-list/`, { list_id: listId, notes });
    return response.data;
  },

  // Remove from a specific list
  async removeFromList(fundingId, listId) {
    const response = await api.delete(`/funding-schemes/${fundingId}/remove-from-list/${listId}/`);
    return response.data;
  },

  // Update notes on a list item
  async updateItemNotes(itemId, notes) {
    const response = await api.patch(`/funding-list-items/${itemId}/`, { notes });
    return response.data;
  },

  // Retrieve all funding lists
  async getListIndex() {
    const response = await api.get('/funding-lists/index/');
    return response.data;
  },

  // Retrieve the funding lists containing a specific action
  async getListMembership(fundingId) {
      const response = await api.get(`/funding-schemes/${fundingId}/list-membership/`);
      return response.data;
  },

  // Rename a list (PATCH on the ModelViewSet)
  async renameList(listId, name) {
    const response = await api.patch(`/funding-lists/${listId}/`, { name });
    return response.data;
  },

  // Duplicate a list (your existing copy action)
  async duplicateList(listId, name) {
    const response = await api.post(`/funding-lists/${listId}/copy/`, { name });
    return response.data;
  },
};

// Alerts API
export const alertsAPI = {
  async toggleScheme(schemeId) {
    const response = await api.post('/alerts/scheme/toggle/', { scheme: schemeId });
    return response.data;
  },
  async checkScheme(schemeId) {
    const response = await api.get(`/alerts/scheme/check/${schemeId}/`);
    return response.data;
  },
  async listSchemeAlerts() {
    const response = await api.get('/alerts/scheme/list/');
    return response.data;
  },
  async toggleCollection(collectionId) {
    const response = await api.post('/alerts/collection/toggle/', { fund_collection: collectionId });
    return response.data;
  },
  async checkCollection(collectionId) {
    const response = await api.get(`/alerts/collection/check/${collectionId}/`);
    return response.data;
  },
  async listCollectionAlerts() {
    const response = await api.get('/alerts/collection/list/');
    return response.data;
  },
  async toggleFunder(organisationId) {
    const response = await api.post('/alerts/funder/toggle/', { organisation: organisationId });
    return response.data;
  },
  async checkFunder(organisationId) {
    const response = await api.get(`/alerts/funder/check/${organisationId}/`);
    return response.data;
  },
  async listFunderAlerts() {
    const response = await api.get('/alerts/funder/list/');
    return response.data;
  },
};

// Notifications API
export const notificationsAPI = {
  async list(unreadOnly = false) {
    const params = unreadOnly ? '?unread=true' : '';
    const response = await api.get(`/notifications/${params}`);
    return response.data;
  },
  async unreadCount() {
    const response = await api.get('/notifications/unread-count/');
    return response.data;
  },
  async markRead(notificationId) {
    const response = await api.post(`/notifications/${notificationId}/read/`);
    return response.data;
  },
  async markAllRead() {
    const response = await api.post('/notifications/read-all/');
    return response.data;
  },
};

// Farm Management API functions
export const farmAPI = {
  // Search farm by SBI
  async searchBySBI(sbi) {
    const response = await api.get(`/farms/search_by_sbi/?sbi=${encodeURIComponent(sbi)}`);
    return response.data;
  },

  // Get all farms (for agents)
  async getAllFarms(search = '') {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await api.get(`/farms/all_farms_for_advisors/${params}`);
    return response.data;
  },

  // Get agent's managed farms
  async getMyFarms() {
    const response = await api.get('/farms/my_farms/');
    return response.data;
  },

  // Create new farm
  async createFarm(farmData) {
    const response = await api.post('/farms/', farmData);
    return response.data;
  },

  // Get farm details
  async getFarm(farmId) {
    const response = await api.get(`/farms/${farmId}/`);
    return response.data;
  },

  // Update farm
  async updateFarm(farmId, farmData) {
    const response = await api.put(`/farms/${farmId}/`, farmData);
    return response.data;
  },

  // Delete farm
  async deleteFarm(farmId) {
    const response = await api.delete(`/farms/${farmId}/`);
    return response.data;
  },
};

export const farmAccessAPI = {
  async assignUserToFarm(farmId, userId, accessType = 'farmer') {
    const response = await api.post('/farm-access/assign/', {
      farm_id: farmId,
      user_id: userId,
      access_type: accessType,
    });
    return response.data;
  },

  async removeUserFromFarm(farmId, userId) {
    const response = await api.post('/farm-access/remove/', {
      farm_id: farmId,
      user_id: userId,
    });
    return response.data;
  }
};

export default api;